/**
 * The user's choices about offline listening: whether the buffer may spend
 * mobile data, and which books they have asked to have whole.
 *
 * Cellular defaults on: the cellular window is fifteen minutes, close to what
 * an ordinary streaming player consumes anyway, which is what makes fetching
 * it unprompted defensible. The switch exists for the user on a small plan
 * who would still rather it did not.
 *
 * A pin is the explicit side of offline listening: "download this book". It
 * records the voice selected when it was made, because a download holds one
 * voice and the scheduler needs to know which. The pins are the whole record
 * of what the user asked for; what is actually on disk is the asset store's
 * business, and progress is derived from the two rather than stored.
 *
 * Same shape as the analytics consent module: a snapshot, listeners, and a
 * hook over useSyncExternalStore. localStorage rather than cookies, because
 * on iOS the app runs from capacitor://localhost where cookie writes drop.
 */

export interface PinnedBook {
  bookId: string;
  /** The voice selected when the pin was made; the download holds this one. */
  voiceKey: string;
  pinnedAt: number;
}

export interface OfflineSettings {
  /** May the look-ahead buffer fetch over a cellular connection? */
  cellularBuffering: boolean;
  /** Books the user asked to have whole, oldest pin first. */
  pinnedBooks: readonly PinnedBook[];
}

const STORAGE_KEY = 'aleph:offline-settings:v1';

export const DEFAULT_OFFLINE_SETTINGS: OfflineSettings = Object.freeze({
  cellularBuffering: true,
  pinnedBooks: Object.freeze([]) as readonly PinnedBook[],
});

const listeners = new Set<() => void>();

// useSyncExternalStore compares snapshots by identity, so the object is built
// once per change and handed out until something invalidates it.
let snapshot: OfflineSettings | null = null;

const isPinnedBook = (value: unknown): value is PinnedBook => {
  const pin = value as PinnedBook | null;
  return (
    !!pin &&
    typeof pin.bookId === 'string' && pin.bookId.length > 0 &&
    typeof pin.voiceKey === 'string' && pin.voiceKey.length > 0 &&
    typeof pin.pinnedAt === 'number' && Number.isFinite(pin.pinnedAt)
  );
};

/** Keep the well-formed pins, one per book, in their stored order. */
const readPinnedBooks = (value: unknown): readonly PinnedBook[] => {
  if (!Array.isArray(value)) {
    return DEFAULT_OFFLINE_SETTINGS.pinnedBooks;
  }
  const pins: PinnedBook[] = [];
  const seen = new Set<string>();
  for (const entry of value) {
    if (isPinnedBook(entry) && !seen.has(entry.bookId)) {
      seen.add(entry.bookId);
      pins.push({ bookId: entry.bookId, voiceKey: entry.voiceKey, pinnedAt: entry.pinnedAt });
    }
  }
  return Object.freeze(pins);
};

const read = (): OfflineSettings => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return DEFAULT_OFFLINE_SETTINGS;
    }
    const parsed = JSON.parse(raw) as Partial<OfflineSettings> | null;
    // Field by field: a malformed value costs that one setting its stored
    // choice, never the others.
    return {
      cellularBuffering:
        typeof parsed?.cellularBuffering === 'boolean'
          ? parsed.cellularBuffering
          : DEFAULT_OFFLINE_SETTINGS.cellularBuffering,
      pinnedBooks: readPinnedBooks(parsed?.pinnedBooks),
    };
  } catch {
    return DEFAULT_OFFLINE_SETTINGS;
  }
};

export const getOfflineSettings = (): OfflineSettings => {
  if (snapshot === null) {
    snapshot = read();
  }
  return snapshot;
};

const commit = (next: OfflineSettings): void => {
  // The choice holds for this session even if the write fails (private mode,
  // storage full); it just will not survive a relaunch.
  snapshot = next;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Nothing to do; see above.
  }
  for (const listener of listeners) {
    listener();
  }
};

export const setCellularBuffering = (enabled: boolean): void => {
  if (getOfflineSettings().cellularBuffering === enabled) {
    return;
  }
  commit({ ...getOfflineSettings(), cellularBuffering: enabled });
};

// ---------------------------------------------------------------------------
// Pins

export const getPinnedBook = (bookId: string): PinnedBook | null =>
  getOfflineSettings().pinnedBooks.find((pin) => pin.bookId === bookId) ?? null;

export const isBookPinned = (bookId: string): boolean => getPinnedBook(bookId) !== null;

/**
 * Ask for a book to be held whole, in the given voice. Pinning a book that
 * is already pinned changes nothing, including its voice: a download holds
 * one voice, and switching it is a non-goal.
 */
export const pinBook = (bookId: string, voiceKey: string, now: number = Date.now()): void => {
  if (!bookId || !voiceKey || isBookPinned(bookId)) {
    return;
  }
  const settings = getOfflineSettings();
  commit({ ...settings, pinnedBooks: Object.freeze([...settings.pinnedBooks, { bookId, voiceKey, pinnedAt: now }]) });
};

export const unpinBook = (bookId: string): void => {
  const settings = getOfflineSettings();
  if (!settings.pinnedBooks.some((pin) => pin.bookId === bookId)) {
    return;
  }
  commit({ ...settings, pinnedBooks: Object.freeze(settings.pinnedBooks.filter((pin) => pin.bookId !== bookId)) });
};

/** Forget every pin. What is on disk is the store's to clear. */
export const clearPinnedBooks = (): void => {
  const settings = getOfflineSettings();
  if (settings.pinnedBooks.length === 0) {
    return;
  }
  commit({ ...settings, pinnedBooks: DEFAULT_OFFLINE_SETTINGS.pinnedBooks });
};

export const subscribeToOfflineSettings = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
